import { Dispatch, SetStateAction, useCallback } from 'react';

import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import { IBackendCartEntries, ILegacyCartEntry, IServerOrder } from '@rbi-ctg/menu';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import { IUseCart } from 'hooks/use-cart-types';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { TRANSITION_ANIMATION_TIME_MS } from 'utils/constants';
import { isWeb } from 'utils/environment';
import { routes } from 'utils/routing';

import { IPendingReorder } from '../types';

type UseHandleReorderInput = {
  addToCart: IUseCart['addItemToCart'];
  setPendingReorder: Dispatch<SetStateAction<IPendingReorder | null>>;
  setReordering: (reordering: boolean) => void;
  setUnavailableCartEntries: (unavailableCartEntries: ILegacyCartEntry[]) => void;
  setReorderedOrderId: Dispatch<SetStateAction<string | null>>;
};

/**
 *
 * Returns a function to reorder a previous order.
 */
export const useHandleReorder = ({
  addToCart,
  setPendingReorder,
  setReordering,
  setUnavailableCartEntries,
  setReorderedOrderId,
}: UseHandleReorderInput) => {
  const { formatMessage } = useIntl();
  const mapBackendToFrontend = useMapToFrontendCartEntry();
  const enableAddExtras = useFlag(LaunchDarklyFlag.ENABLE_ADD_EXTRAS);
  const { sections: addOnSections } = useFeatureMenuAddOn();

  const router = useRouter();

  // This will force to wait for hydration to finish before going to the cart,
  // Otherwise the cart will check for empty entries and will redirect back to the menu
  const redirect = useCallback((url: string) => setTimeout(() => router.replace(url), 2000), [
    router,
  ]);

  const shouldDisplayWebToast = isWeb;

  const hasAddons = addOnSections.length > 0;

  const processEntry = useCallback(
    async (entry: IBackendCartEntries, reorderCartEntries: IBackendCartEntries[]) => {
      const { cartEntry: frontendEntry, status } = await mapBackendToFrontend(
        entry,
        (reorderCartEntries as unknown) as ILegacyCartEntry[]
      );

      return { frontendEntry, status };
    },
    [mapBackendToFrontend]
  );

  return useCallback(
    async (order: IServerOrder, url = routes.orders) => {
      if (!order.cart.reorderCartEntries) {
        // if there's no order, navigate to where was initially intended
        redirect(url);
        return;
      }

      setReordering(true);
      setReorderedOrderId(order.rbiOrderId);

      const { reorderCartEntries } = order.cart;

      const availableEntries: ILegacyCartEntry[] = [];
      const unavailableEntries: ILegacyCartEntry[] = [];

      const processedEntries = await Promise.all(
        reorderCartEntries.map(entry => processEntry(entry, reorderCartEntries))
      );

      for (const entry of processedEntries) {
        const { frontendEntry, status } = entry;

        if (status === ItemAvailabilityStatus.AVAILABLE) {
          availableEntries.push(frontendEntry);
        } else {
          unavailableEntries.push(frontendEntry);
        }
      }

      availableEntries.forEach((entry: ILegacyCartEntry) =>
        addToCart(entry, undefined, !shouldDisplayWebToast)
      );

      if (availableEntries.length && shouldDisplayWebToast) {
        const [firstEntry] = availableEntries;

        const message =
          availableEntries.length > 1
            ? formatMessage(
                { id: 'addToCartMultipleSuccess' },
                { itemName: firstEntry.name, quantity: availableEntries.length - 1 }
              )
            : formatMessage({ id: 'addToCartSuccess' }, { itemName: firstEntry.name });

        toaster.addToCartToast(message, firstEntry.image);
      }

      setReordering(false);
      setReorderedOrderId(null);
      setPendingReorder(null);

      const allEntriesAreUnavailable = unavailableEntries.length === reorderCartEntries.length;

      // If some of the entries are available, we must add them to the cart for reordering.
      if (allEntriesAreUnavailable) {
        setUnavailableCartEntries(unavailableEntries);
        redirect(url);
        return;
      }

      const containsExtras = processedEntries.some(
        entry => entry.status === ItemAvailabilityStatus.CART_EXTRA
      );

      if (enableAddExtras && hasAddons && containsExtras) {
        await new Promise(resolve => setTimeout(resolve, TRANSITION_ANIMATION_TIME_MS)); // just in case
        // If the order had extras go to the extras modal in the cart (but after we get there from previous step)
        redirect(routes.cartAddExtras);
        return;
      }

      redirect(routes.cart);
      return;
    },
    [
      setReordering,
      setReorderedOrderId,
      shouldDisplayWebToast,
      setPendingReorder,
      enableAddExtras,
      hasAddons,
      redirect,
      processEntry,
      addToCart,
      formatMessage,
      setUnavailableCartEntries,
    ]
  );
};
